// ─── HelpCenterPage ───────────────────────────────────────────────────────────
// Public /help page. Content is DYNAMIC: it renders the admin-authored CMS page
// with slug "help" (Admin Portal → CMS Pages → Help Center), the same way the
// generic /page/<slug> route (CmsContentPage) does — admin edits appear here
// immediately. The admin-authored HTML is DOMPurify-sanitised before injection.
//
// The marketing/app chrome is preserved: <Navigation /> always, <AppHeader />
// additionally when authenticated (so logged-in users keep the app tab bar),
// and <Footer />.
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from 'react'
import DOMPurify from 'dompurify'
import { Loader } from 'lucide-react'
import { Navigation } from '../components/Navigation'
import { AppHeader } from '../components/AppHeader'
import { Footer } from '../components/Footer'
import useAuthStore from '../store/auth-store'
import { fetchPublicCmsPage } from '../lib/cmsApi'

// The CMS slug backing this page. Matches the /help route name and the slug set
// on the "Help Center" row in Admin → CMS Pages.
const HELP_SLUG = 'help'

export default function HelpCenterPage() {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const [page, setPage] = useState(null)
    const [status, setStatus] = useState('loading') // loading | ok | notfound | error

    useEffect(() => {
        let cancelled = false
        setStatus('loading')
        fetchPublicCmsPage(HELP_SLUG)
            .then((data) => { if (cancelled) return; setPage(data); setStatus('ok') })
            .catch((err) => {
                if (cancelled) return
                setStatus(err?.response?.status === 404 ? 'notfound' : 'error')
            })
        return () => { cancelled = true }
    }, [])

    useEffect(() => {
        if (page?.title) document.title = `${page.title} — WQData LIVE`
    }, [page])

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <Navigation />
            {isAuthenticated && <AppHeader />}

            <main className="flex-1 max-w-[900px] w-full mx-auto px-4 sm:px-6 py-12">
                {status === 'loading' && (
                    <div className="flex justify-center py-20"><Loader className="w-6 h-6 animate-spin text-[#358ECD]" /></div>
                )}
                {status === 'notfound' && (
                    <div className="text-center py-20">
                        <h1 className="text-2xl font-bold text-[#4D4D4D] mb-2">Page not found</h1>
                        <p className="text-gray-500">This page doesn’t exist or hasn’t been published.</p>
                    </div>
                )}
                {status === 'error' && (
                    <div className="text-center py-20 text-red-600">Something went wrong loading this page.</div>
                )}
                {status === 'ok' && page && (
                    <article>
                        <h1 className="text-3xl font-bold text-[#4D4D4D] mb-6">{page.title}</h1>
                        <div
                            className="cms-content prose max-w-none text-[#4D4D4D]"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content || '') }}
                        />
                    </article>
                )}
            </main>

            <Footer />
        </div>
    )
}
